import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaEnvelope, FaKey, FaArrowLeft } from "react-icons/fa";

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        // Validate email format
        if (!validateEmail(email)) {
            setError("Please enter a valid email address.");
            setLoading(false);
            return;
        }

        try {
            // Configure custom action URL
            const actionCodeSettings = {
                url: 'https://www.x-maths.com/reset-password',
                handleCodeInApp: false, // Set to false to open link in browser
            };

            await sendPasswordResetEmail(auth, email, actionCodeSettings);
            setSuccess("Password reset email sent! Please check your inbox and follow the instructions to reset your password.");
            setEmail("");
        } catch (err) {
            console.error("Password reset error:", err);

            switch (err.code) {
                case "auth/invalid-email":
                    setError("Invalid email address format.");
                    break;
                case "auth/user-not-found":
                    setError("No account found with this email address.");
                    break;
                case "auth/too-many-requests":
                    setError("Too many requests. Please try again later.");
                    break;
                case "auth/network-request-failed":
                    setError("Network error. Please check your internet connection.");
                    break;
                default:
                    setError(`Failed to send reset email: ${err.message}`);
                    break;
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        if (value && !validateEmail(value)) {
            setError("Please enter a valid email address.");
        } else {
            setError("");
        }
    };

    return (
        <div className="font-sans bg-gray-900 text-white min-h-screen">
            <Navbar />

            <section className="relative overflow-hidden py-20">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-sky-900/30 -z-10"></div>
                <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-center gap-12">
                    {/* Visual Element */}
                    <div className="hidden lg:flex lg:w-1/2 justify-center items-center">
                        <div className="relative w-full max-w-lg">
                            <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-600 rounded-full filter blur-3xl opacity-30"></div>
                            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-sky-600 rounded-full filter blur-3xl opacity-30"></div>
                            <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-12 shadow-2xl backdrop-blur-sm">
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-sky-600 rounded-full flex items-center justify-center mb-6">
                                        <FaKey className="text-4xl text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-3">Reset Password</h3>
                                    <p className="text-gray-400">
                                        Enter your email address and we'll send you a link to reset your password
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Section */}
                    <div className="w-full lg:w-1/2 max-w-md">
                        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
                            <h2 className="text-3xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-300">
                                Forgot Password
                            </h2>

                            {error && (
                                <div className="mb-6 p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700 text-sm text-center">
                                    {error}
                                </div>
                            )}

                            {success && (
                                <div className="mb-6 p-4 bg-green-900/50 text-green-300 rounded-lg border border-green-700 text-sm text-center">
                                    {success}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FaEnvelope className="text-gray-500" />
                                        </div>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            onBlur={() => {
                                                if (email && !validateEmail(email)) {
                                                    setError("Please enter a valid email address.");
                                                }
                                            }}
                                            placeholder="Enter your email"
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            required
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-medium transition-all duration-300 shadow-lg hover:shadow-blue-500/30 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : null}
                                    {loading ? "Sending..." : "Send Reset Link"}
                                </button>
                            </form>

                            <div className="mt-6 text-center text-sm text-gray-400">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center hover:text-blue-400 hover:underline transition mb-4"
                                >
                                    <FaArrowLeft className="mr-2" />
                                    Back to Login
                                </Link>
                                <p className="mt-4">
                                    Remember your password?{" "}
                                    <Link
                                        to="/login"
                                        className="text-blue-400 font-medium hover:text-blue-300 hover:underline transition"
                                    >
                                        Sign in
                                    </Link>
                                </p>
                                <p className="mt-2">
                                    Don't have an account?{" "}
                                    <Link
                                        to="/signup"
                                        className="text-blue-400 font-medium hover:text-blue-300 hover:underline transition"
                                    >
                                        Create account
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ForgotPassword;

